import { Link } from 'react-router';
import { Kanban, Workflow } from 'lucide-react';
import { useSession } from '@/contexts/SessionContext';
import { isActivePersona, type FrontendKey } from '@septima/contract';
import { FRONTENDS, sectionForPathname, visibleSectionsFor } from '@/lib/frontends';
import { usePathname } from '@/lib/navigation';
import { useReviewQueueCounts } from '@/lib/use-review-queue-counts';

interface StaffTopBarProps {
    frontend: FrontendKey;
}

// Title-case the first path segment after the `/<frontend>/` prefix, so a route
// with no sidebar row still gets a readable breadcrumb (e.g.
// `/view/fund-management` → "Fund Management"). Falls back to the frontend short
// label for the bare frontend index.
function humanisePathLabel(pathname: string, frontend: FrontendKey): string {
    const rest = pathname.replace(/^\/[^/]+\/?/, '');
    const seg = rest.split('/')[0];
    if (!seg) return FRONTENDS[frontend].shortLabel;
    return seg
        .split('-')
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(' ');
}

// The desktop workspace top bar: a breadcrumb of the current surface plus the
// Agents (kanban, with the pending-review count) affordance. Mobile uses
// `MobileTopBar` instead (this is `hidden lg:flex`). Chrome is flat and
// token-driven — a hairline bottom divider, no shadow.
export default function StaffTopBar({ frontend }: StaffTopBarProps) {
    const pathname = usePathname();
    const { user } = useSession();
    // 'agents' carries the unified pending-review count — review now happens on
    // the Agents kanban's "Needs Review" column (the standalone review-queue
    // route was retired), so the count rides the Agents button.
    const { itemCount } = useReviewQueueCounts(frontend);
    // Prefer the matching sidebar section's label; otherwise humanise the first
    // path segment after the frontend prefix (so a route with no sidebar row —
    // e.g. `/view/fund-management` — reads "Fund Management", not a duplicated
    // frontend name like "View / View").
    const section = sectionForPathname(frontend, pathname);
    const currentLabel = section?.label ?? humanisePathLabel(pathname, frontend);

    // Only surface the Agents button when the active persona may actually see
    // the kanban — mirrors the sidebar's visibility policy (ADR-0013) rather
    // than assuming every persona has it, so the button can never link a
    // persona to a page the backend would 403.
    const activePersona =
        user?.activePersona && isActivePersona(user.activePersona) ? user.activePersona : frontend;
    const visibleSections = visibleSectionsFor(activePersona, user?.personas ?? []);
    const agentsSection = visibleSections.find((section) => section.key === 'agents');
    const workflowsSection = visibleSections.find((section) => section.key === 'workflows');

    const buttonClass =
        'inline-flex items-center gap-1.5 rounded-button border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))] px-3 h-9 text-body-medium text-[rgb(var(--color-text-secondary))] transition-colors hover:bg-[rgb(var(--color-sidebar-hover)/0.04)] hover:text-[rgb(var(--color-text-primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--color-accent)/0.28)]';

    return (
        <header className="hidden lg:flex h-14 flex-none items-center justify-between gap-4 border-b border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))] px-page-padding">
            <nav
                aria-label="Current workspace"
                className="flex items-center gap-1.5 text-body font-medium min-w-0"
            >
                <span className="text-[rgb(var(--color-text-tertiary))]">
                    {FRONTENDS[frontend].shortLabel}
                </span>
                <span className="text-[rgb(var(--color-text-tertiary)/0.6)]">/</span>
                <span className="truncate text-[rgb(var(--color-text-primary))]">
                    {currentLabel}
                </span>
            </nav>

            <div className="flex items-center gap-2">
                {agentsSection && (
                    <Link to={agentsSection.href} className={buttonClass}>
                        <Kanban size={15} />
                        <span>Agents</span>
                        {itemCount > 0 && (
                            <span className="ml-0.5 min-w-5 rounded-full bg-[rgb(var(--color-text-primary)/0.08)] px-1.5 py-0.5 text-center text-annotation tabular-nums text-[rgb(var(--color-text-primary))]">
                                {itemCount}
                            </span>
                        )}
                    </Link>
                )}
                {workflowsSection && (
                    <Link to={workflowsSection.href} className={buttonClass}>
                        <Workflow size={15} />
                        <span>Workflows</span>
                    </Link>
                )}
            </div>
        </header>
    );
}
